import { HttpErrorResponse, type HttpInterceptorFn, type HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { API_ENDPOINT } from '@core/configs';
import { APP_ROUTES } from '@core/constants';
import { AuthService } from '@core/services/auth.service';
import { isTokenExpired } from '@core/utils/jwt.utils';

const ANONYMOUS_AUTH_ENDPOINTS = new Set<string>([
  API_ENDPOINT.AUTH.LOGIN,
  API_ENDPOINT.AUTH.REFRESH,
  API_ENDPOINT.AUTH.SKIP_LOGIN,
]);

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  if (ANONYMOUS_AUTH_ENDPOINTS.has(request.url)) {
    return next(request);
  }

  const session = auth.currentSession();

  // Proactive expiry: skip the round-trip when we already know the token is stale.
  if (session && isTokenExpired(session.accessToken)) {
    return auth.refreshSession().pipe(
      catchError((refreshError: unknown) => {
        handleRefreshFailure(auth, router);
        return throwError(() => refreshError);
      }),
      switchMap((refreshed) => next(withBearer(request, refreshed.accessToken))),
    );
  }

  const authorizedRequest = session ? withBearer(request, session.accessToken) : request;

  return next(authorizedRequest).pipe(
    catchError((error: unknown) => {
      if (!session || !isExpiredTokenError(error)) {
        return throwError(() => error);
      }
      return auth.refreshSession().pipe(
        catchError((refreshError: unknown) => {
          handleRefreshFailure(auth, router);
          return throwError(() => refreshError);
        }),
        switchMap((refreshed) => next(withBearer(request, refreshed.accessToken))),
      );
    }),
  );
};

function withBearer<T>(request: HttpRequest<T>, token: string): HttpRequest<T> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function isExpiredTokenError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 401;
}

function handleRefreshFailure(auth: AuthService, router: Router): void {
  auth.clearSession();
  const isAdmin = router.url.startsWith('/admin');
  router.navigateByUrl(isAdmin ? APP_ROUTES.ADMIN_LOGIN : APP_ROUTES.ROOT);
}
